import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Sparkles, Save, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

const selectCls = "w-full rounded-xl border border-[#e4d8c4] bg-white px-3 py-2.5 text-sm text-[#00334a]";

const META = {
  welcome: {
    title: "Welcome email",
    desc: "Sent automatically the moment an invited supporter creates their account.",
  },
  milestone: {
    title: "Milestone reached",
    desc: "Sent to every team member when their team crosses 25%, 50%, 75% or 100% of goal.",
  },
};

function TemplateEditor({ tpl, onSaved }) {
  const [form, setForm] = useState({ subject: tpl.subject, heading: tpl.heading, body: tpl.body });
  const [busy, setBusy] = useState(false);
  const meta = META[tpl.key];

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/email-templates/${tpl.key}`, {
        subject: form.subject, heading: form.heading, body: form.body, cta_label: tpl.cta_label,
      });
      toast.success(`${meta.title} template saved`);
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#e4d8c4] bg-white p-5" data-testid={`email-template-${tpl.key}`}>
      <div className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-[#65949c]" />
        <span className="font-heading text-lg font-semibold text-[#00334a]">{meta.title}</span>
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#e8e7d6] px-2 py-0.5 text-[10px] font-bold uppercase text-[#5f6a48]">
          Automatic · Editable
        </span>
      </div>
      <p className="mt-1 text-sm text-[#7c7a6a]">{meta.desc}</p>

      <div className="mt-4 space-y-3">
        <div className="space-y-1.5">
          <Label>Subject line</Label>
          <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid={`template-subject-${tpl.key}`} />
        </div>
        <div className="space-y-1.5">
          <Label>Heading (inside the email)</Label>
          <Input value={form.heading} onChange={(e) => setForm({ ...form, heading: e.target.value })} data-testid={`template-heading-${tpl.key}`} />
        </div>
        <div className="space-y-1.5">
          <Label>Body</Label>
          <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={6} data-testid={`template-body-${tpl.key}`} />
        </div>
        <div className="flex flex-wrap items-center gap-1.5 rounded-xl bg-[#f8f3e9] p-2.5">
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#7c7a6a]"><Info className="h-3.5 w-3.5" /> Placeholders:</span>
          {tpl.placeholders.map((p) => (
            <code key={p} className="rounded-md bg-white px-1.5 py-0.5 text-[11px] font-semibold text-[#c97049] border border-[#e4d8c4]">{p}</code>
          ))}
        </div>
      </div>
      <Button onClick={save} disabled={busy} className="mt-4 rounded-full hf-btn-coral font-bold" data-testid={`template-save-${tpl.key}`}>
        <Save className="mr-2 h-4 w-4" /> {busy ? "Saving…" : "Save template"}
      </Button>
    </div>
  );
}

export default function EmailTab() {
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({ campaign_id: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/email-templates").then(({ data }) => setTemplates(data)).catch(() => {});
    api.get("/campaigns").then(({ data }) => {
      setCampaigns(data);
      if (data.length) setForm((f) => ({ ...f, campaign_id: data[0].id }));
    });
  }, []);

  const onSaved = (updated) =>
    setTemplates((prev) => prev.map((t) => (t.key === updated.key ? updated : t)));

  const send = async (e) => {
    e.preventDefault();
    if (!window.confirm("Send this update to all supporters in the campaign?")) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/campaign-update-email", form);
      setResult(data);
      toast.success(`Sent to ${data.sent} of ${data.recipients} supporters`);
      setForm((f) => ({ ...f, subject: "", message: "" }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-email">
      <div>
        <span className="hf-eyebrow">Supporter journeys</span>
        <h1 className="mt-1 font-heading text-3xl font-semibold text-[#00334a]">Email Journeys</h1>
        <p className="mt-1 text-sm text-[#7c7a6a]">These emails send automatically — edit their wording anytime.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {templates.map((t) => <TemplateEditor key={t.key} tpl={t} onSaved={onSaved} />)}
      </div>

      <div className="rounded-2xl border border-[#e4d8c4] bg-white p-6">
        <div className="flex items-center gap-2 font-heading text-lg font-semibold text-[#00334a]">
          <Mail className="h-5 w-5 text-[#c97049]" /> Send a campaign update
        </div>
        <p className="mt-1 text-sm text-[#7c7a6a]">A one-off message to every supporter enrolled in the selected campaign.</p>
        <form onSubmit={send} className="mt-5 space-y-4" data-testid="campaign-email-form">
          <div className="space-y-1.5">
            <Label>Campaign</Label>
            <select value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })} className={selectCls} required data-testid="email-campaign-select">
              <option value="">Select campaign…</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="space-y-1.5"><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required placeholder="We're almost at our goal!" data-testid="email-subject-input" /></div>
          <div className="space-y-1.5"><Label>Message</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required rows={5} placeholder="Write your update to supporters…" data-testid="email-message-input" /></div>
          <Button type="submit" disabled={busy} className="rounded-full hf-btn-coral font-bold" data-testid="email-send-button">
            <Send className="mr-2 h-4 w-4" /> {busy ? "Sending…" : "Send update"}
          </Button>
        </form>
        {result && (
          <div className="mt-4 rounded-xl bg-[#e8e7d6] p-3 text-sm font-semibold text-[#5f6a48]" data-testid="email-result">
            Sent to {result.sent} of {result.recipients} supporters.
          </div>
        )}
      </div>
    </div>
  );
}
